/// TYPES ///
import { CutShape } from "api/types/sketch";
import { CutCorner } from "api/types/sketch";
import { IShopCutShape } from "api/types/shop_quote/cut_shape";
import { Vector2 } from "api/types/sketch";
/// FUNCTIONS ///
import { adjustVectorToScale } from "../draw/scale";
/// VALUES ///
import { SLAB_ZOOM } from "values/values";

const CLICK_TOLERANCE_PX = 12;

export function getCenterOfCutShape(cut_shape: CutShape): Vector2 {
  let X: number = 0;
  let Y: number = 0;

  for (let i = 0; i < cut_shape.cut_corners.length; i++) {
    X += cut_shape.cut_corners[i].location.X;
    Y += cut_shape.cut_corners[i].location.Y;
  }

  return { X: Math.round(X / cut_shape.cut_corners.length), Y: Math.round(Y / cut_shape.cut_corners.length) };
}

export function checkCutShapeClicked(
  cut_shapes: CutShape[] | IShopCutShape[],
  mouse: Vector2
): CutShape | null {
  const mouses: Vector2[] = [
    { X: mouse.X + 4, Y: mouse.Y + 4 },
    { X: mouse.X - 4, Y: mouse.Y - 4 },
    { X: mouse.X - 4, Y: mouse.Y + 4 },
    { X: mouse.X + 4, Y: mouse.Y - 4 }
  ]
  for (let i = 0; i < cut_shapes.length; i++) {
    if (pointInCutShape(mouses, cut_shapes[i].cut_corners)) {
      return cut_shapes[i];
    }
  }

  let closest_shape: CutShape | IShopCutShape | null = null;
  let closest_distance: number = Number.POSITIVE_INFINITY;
  for (let i = 0; i < cut_shapes.length; i++) {
    const distance: number = getCutShapeEdgeDistance(mouse, cut_shapes[i].cut_corners);
    if (distance <= CLICK_TOLERANCE_PX && distance < closest_distance) {
      closest_distance = distance;
      closest_shape = cut_shapes[i];
    }
  }

  if (closest_shape) {
    return closest_shape;
  }

  return null;
}

function pointInCutShape(points: Vector2[], cut_corners: CutCorner[]) {
  if (!cut_corners || cut_corners.length < 3) {
    return false;
  }
  var inside = false;

  for (let i = 0, j = cut_corners.length - 1; i < cut_corners.length; j = i++) {
    const loc_i: Vector2 = adjustVectorToScale(
      {
        X: cut_corners[i].location.X,
        Y: cut_corners[i].location.Y
      },
      SLAB_ZOOM
    );
    const loc_j: Vector2 = adjustVectorToScale(
      {
        X: cut_corners[j].location.X,
        Y: cut_corners[j].location.Y
      },
      SLAB_ZOOM
    );

    const xi = loc_i.X, yi = loc_i.Y;
    const xj = loc_j.X, yj = loc_j.Y;
    let intersect: boolean = false;
    for (let i = 0; i < points.length; i++) {
      intersect = ((yi > points[i].Y) !== (yj > points[i].Y))
        && (points[i].X < (xj - xi) * (points[i].Y - yi) / (yj - yi) + xi);
      if (intersect)
        break;
    }
    if (intersect) inside = !inside;
  }
  return inside;
}

function getCutShapeEdgeDistance(mouse: Vector2, cut_corners: CutCorner[]): number {
  if (!cut_corners || cut_corners.length < 2) {
    return Number.POSITIVE_INFINITY;
  }

  let min_distance: number = Number.POSITIVE_INFINITY;
  for (let i = 0; i < cut_corners.length; i++) {
    const current_corner: CutCorner = cut_corners[i];
    const next_corner: CutCorner = cut_corners[(i + 1) % cut_corners.length];
    if (!current_corner.location || !next_corner.location) {
      continue;
    }
    const a: Vector2 = adjustVectorToScale(current_corner.location, SLAB_ZOOM);
    const b: Vector2 = adjustVectorToScale(next_corner.location, SLAB_ZOOM);
    const distance: number = distanceToLineSegment(mouse, a, b);
    if (distance < min_distance) {
      min_distance = distance;
    }
  }

  return min_distance;
}

function distanceToLineSegment(point: Vector2, start: Vector2, end: Vector2): number {
  const dx: number = end.X - start.X;
  const dy: number = end.Y - start.Y;
  if (dx === 0 && dy === 0) {
    return Math.hypot(point.X - start.X, point.Y - start.Y);
  }
  const t: number = Math.max(
    0,
    Math.min(1, ((point.X - start.X) * dx + (point.Y - start.Y) * dy) / (dx * dx + dy * dy))
  );
  const projection_x: number = start.X + t * dx;
  const projection_y: number = start.Y + t * dy;
  return Math.hypot(point.X - projection_x, point.Y - projection_y);
}
